import { demoCities, demoComments, demoPoints } from './demo-data'
import { functionErrorMessage } from './functionErrors'
import { isSupabaseConfigured, supabase } from './supabase'
import { normalizeMapBounds, pointInMapBounds, type MapBounds } from './mapBounds'
import type { Json } from '../types/database'
import type { AdminPointSummary, AdminRevision, AdminUserActivity, AdminUserSummary, City, ContributionSummary, MyCommentSummary, PointComment, PointDraft, Profile, VendingPoint } from '../types/domain'

const missingBackendMessage = 'Collega Supabase per completare questa operazione.'

export async function fetchCities(): Promise<City[]> {
  if (!supabase) return [...demoCities].sort((a, b) => a.id - b.id)
  const { data, error } = await supabase.rpc('list_active_cities')
  if (error) throw error
  return ([...(data ?? [])] as City[]).sort((a, b) => a.id - b.id)
}

export async function searchCities(query: string, signal?: AbortSignal): Promise<City[]> {
  if (query.trim().length < 2) return []
  if (!supabase) return demoCities.filter((city) => `${city.name} ${city.province}`.toLowerCase().includes(query.trim().toLowerCase()))
  let request = supabase.rpc('search_cities', { p_query: query.trim().slice(0, 100) })
  if (signal) request = request.abortSignal(signal)
  const { data, error } = await request
  if (error) throw error
  return data ?? []
}

export async function fetchCityById(cityId: number): Promise<City | null> {
  if (!supabase) return demoCities.find((city) => city.id === cityId) ?? null
  const { data, error } = await supabase.rpc('search_cities', { p_city_id: cityId })
  if (error) throw error
  return data?.[0] ?? null
}

/** Stable keyset pagination avoids the Data API's row cap on the national map. */
export async function fetchMapPoints(includeHidden = false, bounds?: MapBounds): Promise<VendingPoint[]> {
  const area = bounds ? normalizeMapBounds(bounds) : null
  if (!supabase) return demoPoints.filter((point) => (includeHidden || point.active) && (!area || pointInMapBounds(point, area)))
    .map((point) => ({ ...point, city_name: demoCities.find((city) => city.id === point.city_id)?.name }))
  const points: VendingPoint[] = []
  let afterId: string | undefined
  while (true) {
    const { data, error } = await supabase.rpc('list_map_points', {
      p_include_hidden: includeHidden, p_after_id: afterId, p_limit: 500,
      p_south: area?.south, p_north: area?.north, p_west: area?.west, p_east: area?.east,
    })
    if (error) throw error
    const page = (data ?? []) as VendingPoint[]
    points.push(...page)
    if (page.length < 500) return points
    afterId = page.at(-1)!.id
  }
}

export async function fetchMapPoint(pointId: string, includeHidden = false): Promise<VendingPoint | null> {
  if (!supabase) return (await fetchMapPoints(includeHidden)).find((point) => point.id === pointId) ?? null
  if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(pointId)) return null
  const { data, error } = await supabase.rpc('list_map_points', { p_point_id: pointId, p_include_hidden: includeHidden, p_limit: 1 })
  if (error) throw error
  return (data?.[0] ?? null) as VendingPoint | null
}

export async function updatePreferredCity(cityId: number | null) {
  if (!supabase) throw new Error(missingBackendMessage)
  const { error } = await supabase.rpc('update_my_preferred_city', { p_city_id: cityId ?? undefined })
  if (error) throw error
}

export async function fetchComments(pointId: string): Promise<PointComment[]> {
  if (!supabase) return demoComments.filter((comment) => comment.point_id === pointId)
  const { data, error } = await supabase.rpc('list_visible_point_comments', { p_point_id: pointId })
  if (error) throw error
  return (data ?? []) as unknown as PointComment[]
}

export async function submitComment(input: { pointId: string; displayName?: string; body: string }) {
  if (!supabase) throw new Error(missingBackendMessage)
  const { data, error } = await supabase.functions.invoke('submit-comment', {
    body: { point_id: input.pointId, display_name: input.displayName, body: input.body },
  })
  if (error) throw new Error(await functionErrorMessage(error, 'Commento non pubblicato. Riprova tra poco.'))
  return data as { comment: PointComment }
}

export async function createPoint(draft: PointDraft) {
  if (!supabase) throw new Error(missingBackendMessage)
  const { data, error } = await supabase.rpc('create_point_with_revision', { p_point: draft as unknown as Json })
  if (error) throw error
  return data as string
}

export async function updatePoint(pointId: string, draft: PointDraft, expectedVersion: number) {
  if (!supabase) throw new Error(missingBackendMessage)
  const { error } = await supabase.rpc('update_point_with_revision', {
    p_point_id: pointId,
    p_point: draft as unknown as Json,
    p_expected_version: expectedVersion,
  })
  if (error) throw error
}

export async function reportClosure(pointId: string, expectedVersion: number) {
  if (!supabase) throw new Error(missingBackendMessage)
  const { error } = await supabase.rpc('report_point_closure', {
    p_point_id: pointId,
    p_expected_version: expectedVersion,
  })
  if (error) throw error
}

export async function restoreAdminPoint(pointId: string, expectedVersion: number) {
  if (!supabase) throw new Error(missingBackendMessage)
  const { error } = await supabase.rpc('admin_restore_point', {
    p_point_id: pointId,
    p_expected_version: expectedVersion,
  })
  if (error) throw error
}

export async function deleteAdminPoint(pointId: string, expectedVersion: number) {
  if (!supabase) throw new Error(missingBackendMessage)
  const { error } = await supabase.rpc('admin_delete_point', {
    p_point_id: pointId,
    p_expected_version: expectedVersion,
  })
  if (error) throw error
}

export async function fetchProfile(): Promise<Profile | null> {
  if (!supabase) return null
  const { data, error } = await supabase.rpc('get_my_profile')
  if (error) throw error
  return ((data ?? [])[0] ?? null) as Profile | null
}

export async function updateDisplayName(displayName: string) {
  if (!supabase) throw new Error(missingBackendMessage)
  const { error } = await supabase.rpc('update_my_display_name', { p_display_name: displayName })
  if (error) throw error
}

export async function fetchMyContributions(): Promise<ContributionSummary[]> {
  if (!supabase) return []
  const pageSize = 200
  const contributions: ContributionSummary[] = []
  let beforeCreatedAt: string | undefined
  let beforeId: string | undefined

  while (true) {
    const { data, error } = await supabase.rpc('list_my_contributions', {
      p_limit: pageSize,
      p_before_created_at: beforeCreatedAt,
      p_before_id: beforeId,
    })
    if (error) throw error
    const page = (data ?? []) as ContributionSummary[]
    contributions.push(...page)
    if (page.length < pageSize) return contributions
    const cursor = page.at(-1)!
    beforeCreatedAt = cursor.created_at
    beforeId = cursor.id
  }
}

export async function fetchAdminRevisions(): Promise<AdminRevision[]> {
  if (!supabase) return []
  const { data, error } = await supabase.rpc('list_admin_revisions', { p_limit: 150 })
  if (error) throw error
  return (data ?? []) as unknown as AdminRevision[]
}

export async function fetchMyComments(): Promise<MyCommentSummary[]> {
  if (!supabase) return []
  const pageSize = 200
  const comments: MyCommentSummary[] = []
  let beforeCreatedAt: string | undefined
  let beforeId: string | undefined
  while (true) {
    const { data, error } = await supabase.rpc('list_my_comments', {
      p_limit: pageSize,
      p_before_created_at: beforeCreatedAt,
      p_before_id: beforeId,
    })
    if (error) throw error
    const page = (data ?? []) as MyCommentSummary[]
    comments.push(...page)
    if (page.length < pageSize) return comments
    const cursor = page.at(-1)!
    beforeCreatedAt = cursor.created_at
    beforeId = cursor.id
  }
}

export async function fetchAdminPoints(): Promise<AdminPointSummary[]> {
  if (!supabase) {
    return demoPoints.map((point) => ({
      id: point.id,
      name: point.name,
      category: point.category,
      address: point.address,
      city_id: point.city_id,
      active: point.active,
      version: point.version,
      has_pending_revision: point.has_pending_revision,
      city_name: demoCities.find((city) => city.id === point.city_id)?.name ?? 'Città',
      city_slug: demoCities.find((city) => city.id === point.city_id)?.slug ?? '',
    }))
  }
  const { data, error } = await supabase.rpc('list_admin_points')
  if (error) throw error
  return (data ?? []) as AdminPointSummary[]
}

export async function fetchAdminUsers(): Promise<AdminUserSummary[]> {
  if (!supabase) return []
  const { data, error } = await supabase.rpc('list_admin_users', { p_limit: 500 })
  if (error) throw error
  return (data ?? []) as unknown as AdminUserSummary[]
}

export async function fetchAdminUserActivity(userId: string): Promise<AdminUserActivity[]> {
  if (!supabase) return []
  const { data, error } = await supabase.rpc('list_admin_user_activity', {
    p_user_id: userId,
    p_limit: 500,
  })
  if (error) throw error
  return (data ?? []) as unknown as AdminUserActivity[]
}

export async function deleteAdminUser(userId: string, removePendingContributions: boolean) {
  if (!supabase) throw new Error(missingBackendMessage)
  const { data, error } = await supabase.functions.invoke('admin-delete-user', {
    body: {
      user_id: userId,
      remove_pending_contributions: removePendingContributions,
    },
  })
  if (error) {
    throw new Error(await functionErrorMessage(error, 'Eliminazione utente non riuscita. Riprova.'))
  }
  return data as {
    deleted_user_id: string
    removed_pending_contributions: boolean
    cleanup: { reverted_pending_revisions: number; hidden_comments: number }
  }
}

export async function fetchRecentComments(): Promise<PointComment[]> {
  if (!supabase) return demoComments
  const { data, error } = await supabase.rpc('list_admin_comments', { p_limit: 100 })
  if (error) throw error
  return (data ?? []) as unknown as PointComment[]
}

export async function approveRevision(revisionId: string) {
  if (!supabase) throw new Error(missingBackendMessage)
  const { error } = await supabase.rpc('approve_point_revision', { p_revision_id: revisionId })
  if (error) throw error
}

export async function amendRevisionField(revisionId: string, field: string, value: Json) {
  if (!supabase) throw new Error(missingBackendMessage)
  const { error } = await supabase.rpc('admin_amend_point_revision', {
    p_revision_id: revisionId,
    p_field: field,
    p_value: value,
  })
  if (error) throw error
}

export async function amendRevisionPosition(revisionId: string, position: [number, number], expectedPosition: [number, number]) {
  if (!supabase) throw new Error(missingBackendMessage)
  const { error } = await supabase.rpc('admin_amend_revision_position', {
    p_revision_id: revisionId,
    p_latitude: position[0],
    p_longitude: position[1],
    p_expected_latitude: expectedPosition[0],
    p_expected_longitude: expectedPosition[1],
  })
  if (error) throw new Error(error.message || 'Posizione non salvata. Riprova.')
}

export async function revertRevision(revisionId: string, adminNote?: string) {
  if (!supabase) throw new Error(missingBackendMessage)
  const { error } = await supabase.rpc('revert_point_revision', { p_revision_id: revisionId, p_admin_note: adminNote || undefined })
  if (error) throw error
}

export async function hideComment(commentId: string, hidden: boolean) {
  if (!supabase) throw new Error(missingBackendMessage)
  const { error } = await supabase.rpc('admin_set_comment_visibility', { p_comment_id: commentId, p_hidden: hidden })
  if (error) throw error
}

export async function setPointActive(pointId: string, active: boolean, expectedVersion: number) {
  if (!supabase) throw new Error(missingBackendMessage)
  const { error } = await supabase.rpc('admin_set_point_active', {
    p_point_id: pointId,
    p_active: active,
    p_expected_version: expectedVersion,
    p_admin_note: active ? 'Punto reso visibile dal pannello amministrativo' : 'Punto nascosto dal pannello amministrativo',
  })
  if (error) throw error
}

export { isSupabaseConfigured }
